import { CommonModule } from '@angular/common';
import { Component, OnInit, output } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CompanyTypeFormGroup,
  MergeLanguagesForm,
} from '@app/models/form.models';
import { CompanyTypeSaveService } from '@app/services/admin/company-type/company-type-save.service';
import { GenericDataService } from '@app/services/generic-data.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CountriesUsedTabComponent } from '../company-type-details/countries-used-tab/countries-used-tab.component';
import { DetailsTabComponent } from '../company-type-details/details-tab/details-tab.component';
import { LocalizationTabComponent } from '../company-type-details/localization-tab/localization-tab.component';
import { MasterdataService } from './../../../../../+services/admin/masterdata.service';

@Component({
  selector: 'app-company-type-add',
  standalone: true,
  templateUrl: './company-type-add.component.html',
  styleUrl: './company-type-add.component.scss',
  imports: [
    CommonModule,
    TranslatePipe,
    DetailsPageHeaderComponent,
    DetailsTabComponent,
    CountriesUsedTabComponent,
    LocalizationTabComponent,
    ButtonComponent,
  ],
})
export class CompanyTypeAddComponent implements OnInit {
  public emitCancel = output<void>();
  public emitSave = output<void>();

  companyTypeFormGroup: FormGroup<CompanyTypeFormGroup>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private service: GenericDataService,
    private saveSvc: CompanyTypeSaveService,
    private masterdataService: MasterdataService
  ) {
    const minOneCountry: ValidatorFn = (g: AbstractControl) => {
      const arr = (g.get('countries')?.value as string[]) ?? [];
      return arr.length > 0 ? null : { minOneCountry: true };
    };
    const localizationTabForm = this.fb.group<{
      [language: string]: FormGroup<MergeLanguagesForm>;
    }>({});
    this.companyTypeFormGroup = this.fb.group({
      details: this.fb.nonNullable.group({
        code: this.fb.nonNullable.control('', {
          validators: [Validators.required, Validators.maxLength(10)],
          asyncValidators: [this.uniqueCodeValidator(() => null)],
          updateOn: 'blur',
        }),
        description: this.fb.control('', [Validators.required]),
      }),
      countriesUsed: this.fb.group(
        {
          countries: this.fb.nonNullable.control<string[]>([]),
        },
        { validators: [minOneCountry] }
      ),
      localizationsTab: localizationTabForm,
    });
  }

  uniqueCodeValidator(getOriginalCode?: () => string | null): AsyncValidatorFn {
    return (control: AbstractControl) => {
      const value = (control.value ?? '').toString().trim();

      if (!value || (getOriginalCode && getOriginalCode() === value)) {
        return of(null);
      }

      return this.masterdataService.loadCompanyTypesCodeFiltered(value).pipe(
        map((res: any) => {
          const exists = (res?.data ?? []).some(
            (t: any) =>
              (t?.code ?? '').toString().toLowerCase() === value.toLowerCase()
          );
          return exists ? ({ codeTaken: true } as ValidationErrors) : null;
        })
      );
    };
  }

  ngOnInit(): void {
    this.service.loadLanguages().subscribe((langs) => {
      const formGroup = this.fb.group<{
        [language: string]: FormGroup<MergeLanguagesForm>;
      }>({});

      for (const item of langs) {
        const isEN = item.code === 'EN';
        formGroup.addControl(
          item.code,
          this.fb.group({
            localizedName: this.fb.nonNullable.control(
              '',
              isEN ? [Validators.required] : []
            ),
            localizedTypeName: this.fb.nonNullable.control(
              '',
              isEN ? [Validators.required] : []
            ),
          })
        );
      }

      this.companyTypeFormGroup.setControl('localizationsTab', formGroup);
    });
  }

  get countriesUsedList(): string[] {
    return this.companyTypeFormGroup.controls.countriesUsed.controls.countries
      .value;
  }

  goBack(): void {
    const target = history.state?.from || '/admin/database/companyType';
    this.router.navigate([target], { relativeTo: this.route });
  }

  save() {
    if (this.companyTypeFormGroup.invalid) {
      this.companyTypeFormGroup.markAllAsTouched();
      this.companyTypeFormGroup.updateValueAndValidity({ emitEvent: true });
      return;
    }
    this.saveSvc.saveFromForms(this.companyTypeFormGroup);
  }
}
